// src/pages/EventSubscription.tsx
import { collection, getDocs, query, where } from "firebase/firestore";
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import EventOverview from "../components/user/EventOverview";
import RegistrationForm from "../components/user/RegistrationForm";
import UnregisterButton from "../components/user/UnregisterButton";
import { useAuth } from "../context/AuthContext";
import { useEventContext } from "../context/EventContext";
import { db } from "../firebaseConfig";

const EventSubscription: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const [loading, setLoading] = useState(true);
  const [currentRegistrations, setCurrentRegistrations] = useState(0);
  const [isUserRegistered, setIsUserRegistered] = useState(false);
  const { user } = useAuth();
  const { events, isEventActive } = useEventContext();

  // Find the current event from context
  const eventData = events.find((event) => event.id === eventId);

  const fetchRegistrations = useCallback(async () => {
    if (!eventId) return;
    const registrationsQuery = query(
      collection(db, `events/${eventId}/registrations`)
    );
    const registrationsSnapshot = await getDocs(registrationsQuery);
    setCurrentRegistrations(registrationsSnapshot.size);
  }, [eventId]);

  const checkUserRegistration = useCallback(async () => {
    if (!user || !eventId) return;
    const q = query(
      collection(db, `events/${eventId}/registrations`),
      where("userId", "==", user.uid)
    );
    const querySnapshot = await getDocs(q);
    setIsUserRegistered(!querySnapshot.empty);
  }, [user, eventId]);

  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      try {
        await Promise.all([fetchRegistrations(), checkUserRegistration()]);
      } catch (error) {
        console.error("Error initializing data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (eventId) {
      initializeData();
    }
  }, [eventId, fetchRegistrations, checkUserRegistration]);

  const handleRegistrationChange = useCallback(async () => {
    if (eventId) {
      await fetchRegistrations();
      await checkUserRegistration();
    }
  }, [eventId, fetchRegistrations, checkUserRegistration]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-primary"></div>
      </div>
    );
  }

  if (!eventData) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 text-center">
        <p className="text-xl font-semibold text-gray-700">
          Event not found. 😢
        </p>
        <p className="mt-2 text-gray-500">
          The event you're looking for might have been removed or doesn't exist.
        </p>
      </div>
    );
  }

  // Check if event is a closed event (requires invitation code)
  const isClosedEvent = eventData.isClosedEvent || false;
  const isEventFull = currentRegistrations >= eventData.maxTickets;

  return (
    <div className="space-y-8">
      <EventOverview
        eventId={eventData.id}
        title={eventData.title}
        description={eventData.description}
        date={eventData.date}
        time={eventData.time}
        address={eventData.address}
      />
      {isEventActive(eventData.id) ? (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="mb-4">
            <div className="flex justify-between mb-2">
              <span className="text-sm font-semibold text-primary">
                Spots filled:
              </span>
              <span className="text-sm font-semibold text-primary">
                {currentRegistrations} / {eventData.maxTickets}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className={`h-2.5 rounded-full ${
                  isEventFull ? "bg-alert" : "bg-secondary"
                }`}
                style={{
                  width: `${
                    (currentRegistrations / eventData.maxTickets) * 100
                  }%`,
                }}
              ></div>
            </div>
          </div>
          
          {/* Display closed event status badge */}
          {isClosedEvent && (
            <div className="bg-gray-100 border-l-4 border-gray-500 p-3 mb-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-gray-700">
                    This is a private event. You need an invitation code to register.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {isUserRegistered ? (
            <div className="space-y-4">
              <p className="text-green-600 text-center">
                You are registered for this event.
              </p>
              <UnregisterButton
                eventId={eventData.id}
                onUnregister={handleRegistrationChange}
              />
            </div>
          ) : isEventFull ? (
            <p className="text-alert text-center">
              Sorry, this event is now full. No more registrations can be
              accepted.
            </p>
          ) : (
            <RegistrationForm
              eventId={eventData.id}
              title={eventData.title}
              maxTickets={eventData.maxTickets}
              currentRegistrations={currentRegistrations}
              isClosedEvent={isClosedEvent}
              onRegistrationChange={handleRegistrationChange}
            />
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md p-6 text-center">
          <p className="text-xl font-semibold text-alert">
            This event is currently not active.
          </p>
          <p className="mt-2 text-gray-500">
            Registration is only available for active events.
          </p>
        </div>
      )}
    </div>
  );
};

export default EventSubscription;